import { CncQuoteForm } from "@/components/CncQuoteForm";
import { Card, Icon, Pill } from "@/components/ui";
import { type CncMasterData, emptyCncSelection } from "@/lib/cost/cncForm";
import type { CncPartSelection } from "@/lib/data";
import { useRef, useState } from "react";

export const CncPartsEditor = ({
  value,
  onChange,
  master,
}: {
  value: CncPartSelection[];
  onChange: (next: CncPartSelection[]) => void;
  master: CncMasterData;
}) => {
  const [openIdx, setOpenIdx] = useState(0);
  // ponytail: ref set once on mount — parts at index >= initialCount are user-added this session
  const initialCount = useRef(value.length);

  const updatePart = (idx: number, sel: CncPartSelection) =>
    onChange(value.map((p, i) => (i === idx ? sel : p)));
  const addPart = () => {
    onChange([...value, emptyCncSelection(master.settings)]);
    setOpenIdx(value.length);
  };
  // ponytail: 복사본은 맨 끝에 추가 — 중간 삽입하면 initialCount 기반 "새 파트" 배지의 인덱스가 밀림
  const duplicatePart = (idx: number) => {
    onChange([...value, structuredClone(value[idx])]);
    setOpenIdx(value.length);
  };
  const removePart = (idx: number) => {
    if (value.length <= 1) return;
    onChange(value.filter((_, i) => i !== idx));
    setOpenIdx((o) => (o >= idx ? Math.max(0, o - 1) : o));
  };

  const materialName = (sel: CncPartSelection) =>
    master.raw.find((r) => r.id === sel.materialId)?.name ?? "재질 미선택";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {value.map((sel, idx) => {
        const open = openIdx === idx;
        return (
          <Card key={idx} pad={0} style={{ overflow: "hidden" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
                padding: "14px 18px",
                cursor: "pointer",
              }}
              onClick={() => setOpenIdx(open ? -1 : idx)}
            >
              <Icon name={open ? "chevD" : "chevR"} size={16} />
              <div style={{ fontWeight: 800, fontSize: 14 }}>
                파트 {idx + 1} · {materialName(sel)}
              </div>
              {idx >= initialCount.current && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: 6,
                    background: "var(--accent-soft, #e8f4ff)",
                    color: "var(--accent, #1a7be8)",
                    whiteSpace: "nowrap",
                  }}
                >
                  사용자가 새로 추가한 파트입니다.
                </span>
              )}
              <div style={{ flex: 1 }} />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  duplicatePart(idx);
                }}
                title="파트 복사"
                style={{
                  background: "transparent",
                  border: 0,
                  color: "var(--text-dim)",
                  cursor: "pointer",
                  display: "inline-flex",
                }}
              >
                <Icon name="copy" size={15} />
              </button>
              {value.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removePart(idx);
                  }}
                  title="파트 삭제"
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "var(--text-dim)",
                    cursor: "pointer",
                    display: "inline-flex",
                  }}
                >
                  <Icon name="trash" size={15} />
                </button>
              )}
            </div>
            {open && (
              <div style={{ padding: "0 18px 18px" }}>
                <CncQuoteForm
                  value={sel}
                  onChange={(next) => updatePart(idx, next)}
                  master={master}
                />
              </div>
            )}
          </Card>
        );
      })}
      <div>
        <Pill variant="dark" size="sm" icon={<Icon name="plus" size={15} />} onClick={addPart}>
          파트 추가
        </Pill>
      </div>
    </div>
  );
};
